import React, { useState, useRef, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTheme } from "../contexts/ThemeSelection";
import { useUser } from "../contexts/UserContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import useFocusMode from "../hooks/useFocusMode";
import usePregnancy from "../contexts/PregnancyContext";
import { ALL_NAV_ITEMS, DEFAULT_NAV_BAR_ITEMS, useAvailableNavItems } from "../hooks/useNavItems";
import styles from "../styles/HamburgerMenu.module.css";

interface HamburgerMenuProps {
  iconSize: string;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ iconSize }) => {
  const { activeTheme, theme } = useTheme();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const userContext = useUser();
  const { settings } = useUserSettings();
  const availableItems = useAvailableNavItems();
  const { focusModeEnabled, profile: focusProfile } = useFocusMode();
  const { isPregnant } = usePregnancy();

  const navBarItemIds = new Set(settings.navBarItems ?? DEFAULT_NAV_BAR_ITEMS);

  const linkStyle: React.CSSProperties = { color: theme[activeTheme]?.color };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const menuItems = useMemo(() => {
    let overflowItems: { to: string; icon: string; label: string }[];

    if (focusModeEnabled && focusProfile) {
      // In focus mode, show Account + Family only. Focus tabs handle the rest.
      overflowItems = [
        { to: "/account", icon: "fa-solid fa-user", label: "Account" },
        { to: "/family", icon: "fa-solid fa-people-group", label: "Family & Friends" },
      ];
    } else {
      // Show available items that are NOT in the nav bar
      overflowItems = ALL_NAV_ITEMS
        .filter((item) => availableItems.has(item.id) && !navBarItemIds.has(item.id))
        .map((item) => {
          const label = isPregnant && item.id === "womens-health" ? "Baby" : item.label;
          const icon = isPregnant && item.id === "womens-health" ? "fa-solid fa-baby" : item.icon;
          return { to: item.path, icon, label };
        });
    }

    // Admin is always in the hamburger menu
    if (userContext?.userDetails?.admin) {
      overflowItems.push({ to: "/admin", icon: "fa-solid fa-shield-halved", label: "Admin" });
    }

    return overflowItems;
  }, [availableItems, navBarItemIds, userContext?.userDetails?.admin, focusModeEnabled, focusProfile, isPregnant]);

  return (
    <div className={styles.menuWrapper} ref={panelRef}>
      <button
        className={styles.menuButton}
        onClick={() => setOpen((prev) => !prev)}
        style={{ color: theme[activeTheme].color }}
      >
        <i className="fa-solid fa-bars" style={{ fontSize: iconSize }} />
      </button>

      {open && (
        <>
          <div className={styles.overlay} onClick={() => setOpen(false)} />
          <div
            className={styles.panel}
            style={{
              backgroundColor: theme[activeTheme].panelColor,
              color: theme[activeTheme].color,
            }}
          >
            <div
              className={styles.panelHeader}
              style={{ background: theme[activeTheme].heroGradient }}
            >
              <div className={styles.panelBrand}>
                <img
                  src={`${process.env.PUBLIC_URL}/icons/icon-96x96.png`}
                  alt="OnyxLife"
                  className={styles.panelLogo}
                />
                <span className={styles.panelTitle}>OnyxLife</span>
              </div>
              <button
                className={styles.closeButton}
                style={{ color: "#fff" }}
                onClick={() => setOpen(false)}
              >
                &times;
              </button>
            </div>
            {menuItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={styles.menuItem}
                style={linkStyle}
                onClick={() => setOpen(false)}
              >
                <span className={styles.menuItemIcon}>
                  <i className={item.icon} />
                </span>
                <span className={styles.menuItemLabel}>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HamburgerMenu;
